import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { formatJobNumber } from '@fieldops/shared'
import { cn } from '../../lib/cn.js'
import { durationBetween } from '../../lib/format.js'
import { stageOf, stageStatus } from '../../lib/job-stages.js'
import { useIsNarrow } from '../../lib/media.js'
import { useSession } from '../../lib/session.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { AgreementBuilder } from '../agreements/AgreementBuilder.js'
import { EmailCompose } from '../email/EmailCompose.js'
import { Icon, type IconName } from '../Icon.js'
import { Sheet } from '../Sheet.js'
import { Button, ErrorState, SkeletonRows } from '../ui.js'
import { Wordmark } from '../Wordmark.js'
import { ProposalBuilder } from '../proposal/ProposalBuilder.js'
import { ActivityPane } from './ActivityPane.js'
import { AssistantPane } from './AssistantPane.js'
import { BillingPane } from './BillingPane.js'
import { BookingPopup } from './BookingPopup.js'
import { BriefPane } from './BriefPane.js'
import { ChecklistPane } from './ChecklistPane.js'
import { ClientPane, ClientRail } from './ClientPane.js'
import { isProposal } from './feed.js'
import { DropStatus, DropVeil, useFileDrop } from './FileDrop.js'
import { FilesPane } from './FilesPane.js'
import { JobDetailsCard } from './JobDetailsCard.js'
import { NoteCompose } from './NoteCompose.js'
import { NotBuiltYet, Panel, emailContacts, nextVisit, primaryPerson } from './panes.js'
import { SiteAndClient } from './RecordCards.js'
import { RepeatPopup, type RepeatPopupMode } from './RepeatPopup.js'
import { SchedulePane, ScheduleRail } from './SchedulePane.js'
import { ServicesPane, type ReportPdf } from './ServicesPane.js'
import { openServices, reportEmailDraft } from './services.js'
import { SmsCompose } from './SmsCompose.js'
import type { BookingEditor, Job } from './types.js'

type TabKey = 'work' | 'assistant' | 'billing' | 'schedule' | 'client' | 'files' | 'forms'

/**
 * The job card.
 *
 * Always a popup, opened over whatever the person was looking at, by
 * `JobPopup`. The dispatch board's reasoning turned out to be everyone's: the
 * day a dispatcher is holding in their head has to survive reading a job, and
 * so does the list an office manager is working down. There is no page.
 * Decision 61.
 *
 * Below `lg` it is a third thing: a sheet that slides up over the app's tab
 * bar, with the header in the scroll flow, the tab strip sticky, the
 * actions as the bar along the bottom and the activity column behind a button
 * that opens a second sheet. That is a different tree rather than the same one
 * restyled, which is why it is a `useIsNarrow` branch and not `lg:` classes —
 * the feed cannot be in a column and in a sheet at once. Decision 58.
 *
 * The shape is the Navy command bar mockup (ADR 025): a Buster Navy header of
 * the emblem, the number, the kind of work as a red tag and the client and
 * suburb under them, then a white tab strip and a white body. The number leads
 * — it is what staff quote on the phone. The header says *which* job; the tag
 * only reads the kind of work, and what state the job is in and the picker for
 * the kind of work are the job's own record, in `JobDetailsCard`, the first
 * panel on Work.
 * **The activity column is only on Work.** Client, Files and Forms take the
 * full width so the record is not squeezed beside a feed nobody came to that
 * tab to read. Billing replaces the feed with a billing rail of totals and
 * actions — the quote mockup's shape — because the number that matters on
 * that tab is the total, not the last note. A note is still a tap away on a
 * phone (the Activity sheet) and a tab away on a desk. Columns that are on
 * screen together are named regions, because the quote number still appears
 * in more than one place.
 *
 * Assistant is a conversation about the job and takes the whole width too,
 * on a phone as well — its own tree, not a panel. Decision 94.
 *
 * Client holds the client, the property and the people on the job. Work is
 * the services this job sells, each with its own report, and below them the
 * checklist ServiceM8's template materialised. Decisions 40, 52, 53 and 84.
 */
export function JobCard({
  jobId,
  onClose,
  className,
}: {
  jobId: string
  /** Present on the desktop modal; the phone sheet closes with its own Done. */
  onClose?: () => void
  className?: string
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()
  const narrow = useIsNarrow()

  const [tab, setTab] = useState<TabKey>('work')
  const [editor, setEditor] = useState<BookingEditor | null>(null)
  const [activityOpen, setActivityOpen] = useState(false)
  const [openProposalId, setOpenProposalId] = useState<string | null>(null)
  const [compose, setCompose] = useState<'note' | 'email' | 'sms' | null>(null)
  const [reportEmail, setReportEmail] = useState<ReportPdf | null>(null)

  const canEdit = can('JOB_EDIT')
  const canSchedule = can('SCHEDULE_EDIT')

  const {
    data: job,
    isPending,
    error,
    refetch,
  } = useQuery(trpc.jobs.byId.queryOptions({ id: jobId }))

  /*
   * The kinds of work, for the details panel's picker.
   *
   * Asked for here rather than inside the panel so it goes out in the same tick
   * as the job and `httpBatchLink` folds the two into one request — the reason
   * the card opens quickly from the board is that it costs one. Reference data
   * with a 30-second stale time, so the second job someone opens pays nothing,
   * and it is not asked for at all by somebody who could not act on it.
   */
  const categories = useQuery({
    ...trpc.jobs.categories.queryOptions(),
    enabled: canEdit,
  })

  /*
   * The repeat schedule, for the Schedule tab and the line in Job details.
   * Asked for beside the job for the same reason as the categories: one
   * batched request. Decision 96.
   */
  const repeat = useQuery(trpc.repeat.forJob.queryOptions({ jobId }))
  const [repeatMode, setRepeatMode] = useState<RepeatPopupMode | null>(null)
  const [buildingAgreement, setBuildingAgreement] = useState(false)

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
    void queryClient.invalidateQueries(trpc.schedule.pathFilter())
  }

  const update = useMutation(trpc.jobs.update.mutationOptions({ onSuccess: invalidate }))
  const addNote = useMutation(trpc.jobs.addNote.mutationOptions({ onSuccess: invalidate }))
  const startTimer = useMutation(trpc.jobs.startTimer.mutationOptions({ onSuccess: invalidate }))
  const stopTimer = useMutation(trpc.jobs.stopTimer.mutationOptions({ onSuccess: invalidate }))
  const setTaskDone = useMutation(trpc.jobs.setTaskDone.mutationOptions({ onSuccess: invalidate }))
  const addTask = useMutation(trpc.jobs.addTask.mutationOptions({ onSuccess: invalidate }))
  const addContact = useMutation(trpc.jobs.addContact.mutationOptions({ onSuccess: invalidate }))
  const updateContact = useMutation(
    trpc.jobs.updateContact.mutationOptions({ onSuccess: invalidate }),
  )
  const setBillingContact = useMutation(
    trpc.jobs.setBillingContact.mutationOptions({ onSuccess: invalidate }),
  )
  const removeContact = useMutation(
    trpc.jobs.removeContact.mutationOptions({ onSuccess: invalidate }),
  )
  const setChecklistItemDone = useMutation(
    trpc.jobs.setChecklistItemDone.mutationOptions({ onSuccess: invalidate }),
  )
  const skipChecklistItem = useMutation(
    trpc.jobs.skipChecklistItem.mutationOptions({ onSuccess: invalidate }),
  )
  const removeService = useMutation(
    trpc.jobs.removeService.mutationOptions({ onSuccess: invalidate }),
  )

  const drop = useFileDrop({ jobId, enabled: canEdit, onUploaded: invalidate })

  const frame = cn('flex min-h-0 flex-col bg-white', narrow && 'flex-1', className)

  if (error) {
    return (
      <div className={frame}>
        <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
      </div>
    )
  }

  if (isPending) {
    return (
      <div className={frame}>
        <SkeletonRows />
      </div>
    )
  }

  const next = nextVisit(job)
  const primary = primaryPerson(job)
  const running = job.timeEntries.find((entry) => entry.endAt === null)
  const phone = primary?.phone ?? job.client.phone ?? null
  const email = primary?.email ?? job.client.email ?? null
  const files = job.attachments.length

  /*
   * The actions, described once and drawn twice: as a row of chips in the
   * desktop header, and as the bottom bar on a phone. These are the only
   * Note, SMS and Email buttons for the job; the activity column only reads.
   */
  const actions: ActionSpec[] = [
    {
      icon: 'phone',
      label: 'Call',
      href: phone ? `tel:${phone.replace(/[^\d+]/g, '')}` : null,
    },
    {
      icon: 'sms',
      label: 'SMS',
      onClick: phone ? () => setCompose('sms') : undefined,
    },
    { icon: 'mail', label: 'Email', onClick: () => setCompose('email') },
    ...(canEdit
      ? [
          {
            icon: 'note' as const,
            label: 'Note',
            onClick: () => setCompose('note'),
          },
        ]
      : []),
    {
      icon: 'catalog',
      label: 'Files',
      onClick: files > 0 ? () => setTab('files') : undefined,
      disabledTitle: 'Nothing is attached to this job',
    },
    {
      icon: 'schedule',
      // "Book", not "Schedule": the Schedule tab is the repeat schedule.
      label: 'Book',
      primary: true,
      // On a job with no visit yet this is how one gets made, rather than the
      // action doing nothing at all and sending the office to the board.
      // Somebody who cannot book is shown the bookings instead.
      onClick: () => {
        if (canSchedule) setEditor(next ? { kind: 'edit', visitId: next.id } : { kind: 'create' })
        else if (narrow) setActivityOpen(true)
        else setTab('work')
      },
    },
  ]

  const stage = stageOf(job.status) ?? 'QUOTE'
  const suburb = job.site?.address?.suburb ?? null

  const tabs = (
    <>
      <TabButton active={tab === 'work'} count={openItems(job)} onClick={() => setTab('work')}>
        Work
      </TabButton>
      {can('ASSISTANT_USE') ? (
        <TabButton assistant active={tab === 'assistant'} onClick={() => setTab('assistant')}>
          Assistant
        </TabButton>
      ) : null}
      <TabButton active={tab === 'billing'} onClick={() => setTab('billing')}>
        Billing
      </TabButton>
      <TabButton active={tab === 'schedule'} onClick={() => setTab('schedule')}>
        Schedule
      </TabButton>
      <TabButton active={tab === 'client'} onClick={() => setTab('client')}>
        Client
      </TabButton>
      {files > 0 ? (
        <TabButton active={tab === 'files'} count={files} onClick={() => setTab('files')}>
          Files
        </TabButton>
      ) : null}
      <TabButton active={tab === 'forms'} onClick={() => setTab('forms')}>
        Forms
      </TabButton>
    </>
  )

  const panels =
    tab === 'work' ? (
      <>
        <JobDetailsCard
          job={job}
          stage={stage}
          canEdit={canEdit}
          categories={categories.data ?? []}
          isSaving={update.isPending}
          onChangeStage={(next) => update.mutate({ id: job.id, status: stageStatus(next) })}
          onChangeCategory={(categoryId) => update.mutate({ id: job.id, categoryId })}
          repeat={repeat.data?.schedule ?? null}
          onOpenSchedule={() => setTab('schedule')}
        />

        <SiteAndClient job={job} onOpen={() => setTab('client')} />

        <BriefPane
          job={job}
          canEdit={canEdit}
          onSave={(values) => update.mutateAsync({ id: job.id, ...values })}
        />

        <Panel>
          <ServicesPane
            job={job}
            canEdit={canEdit}
            isAddingNote={addNote.isPending}
            onAddNote={(jobServiceId, body, done) =>
              addNote.mutate({ body, jobId: job.id, jobServiceId }, { onSuccess: done })
            }
            onRemoveService={(id) => removeService.mutate({ id })}
            onEmailReport={setReportEmail}
          />
        </Panel>

        <ChecklistPane
          job={job}
          canEdit={canEdit}
          officeView={can('JOB_VIEW_ALL')}
          isAdding={addTask.isPending}
          onToggleTask={(id, done) => setTaskDone.mutate({ id, done })}
          onAddTask={(title, done) => addTask.mutate({ jobId: job.id, title }, { onSuccess: done })}
          onToggleItem={(id, done) => setChecklistItemDone.mutate({ id, done })}
          onSkipItem={(id, reason, done) =>
            skipChecklistItem.mutate({ id, reason }, { onSuccess: done })
          }
        />

        {canEdit ? (
          <div className="py-3">
            {running ? (
              <Button
                size="sm"
                variant="danger"
                loading={stopTimer.isPending}
                onClick={() => stopTimer.mutate({ id: running.id })}
              >
                Stop · {durationBetween(running.startAt)}
              </Button>
            ) : (
              <Button
                size="sm"
                loading={startTimer.isPending}
                onClick={() => startTimer.mutate({ jobId: job.id })}
              >
                Start timer
              </Button>
            )}
          </div>
        ) : null}
      </>
    ) : tab === 'billing' ? (
      <BillingPane job={job} />
    ) : tab === 'schedule' ? (
      repeat.error ? (
        <ErrorState message={errorMessage(repeat.error)} onRetry={() => void repeat.refetch()} />
      ) : repeat.data ? (
        <SchedulePane
          job={job}
          data={repeat.data}
          canSchedule={canSchedule}
          onStart={(legacyRecurrenceId) => setRepeatMode({ kind: 'start', legacyRecurrenceId })}
          onAgreement={can('CLIENT_EDIT') ? () => setBuildingAgreement(true) : undefined}
        />
      ) : (
        <SkeletonRows rows={4} />
      )
    ) : tab === 'client' ? (
      <ClientPane
        job={job}
        canEdit={canEdit}
        isSavingContact={
          addContact.isPending ||
          updateContact.isPending ||
          setBillingContact.isPending ||
          removeContact.isPending
        }
        onAddContact={(contactId) => addContact.mutate({ jobId: job.id, contactId })}
        onUpdateContact={(input) => updateContact.mutate(input)}
        onSetBilling={(jobContactId) => setBillingContact.mutate({ jobId: job.id, jobContactId })}
        onRemoveContact={(id) => removeContact.mutate({ id })}
      />
    ) : tab === 'files' ? (
      <Panel>
        <FilesPane job={job} />
      </Panel>
    ) : (
      <NotBuiltYet
        title="No forms on this job yet"
        what="Signed treatment records and inspection reports will read here."
        why="Form, FormVersion and FormResponse are all in the schema and 62,202 responses are sitting in the ServiceM8 staging tables, but there is no router to read one with yet."
      />
    )

  const activity = (
    <ActivityPane
      job={job}
      canSchedule={canSchedule}
      onEditor={setEditor}
      onOpenQuote={
        can('QUOTE_VIEW')
          ? (quoteId) => {
              const quote = job.quotes.find((row) => row.id === quoteId)
              if (quote && isProposal(quote)) {
                setOpenProposalId(quoteId)
              } else {
                setActivityOpen(false)
                setTab('billing')
              }
            }
          : undefined
      }
    />
  )

  const proposalBuilder = openProposalId ? (
    <ProposalBuilder
      kind="quote"
      open
      job={job}
      quoteId={openProposalId}
      onClose={() => setOpenProposalId(null)}
    />
  ) : null

  const repeatSchedule = repeat.data?.schedule ?? null
  const scheduleRail =
    repeat.data && repeatSchedule ? (
      <ScheduleRail
        job={job}
        data={repeat.data}
        canSchedule={canSchedule}
        canEditAgreement={can('CLIENT_EDIT')}
        onEdit={() => setRepeatMode({ kind: 'edit', schedule: repeatSchedule })}
      />
    ) : null

  const editingVisit =
    editor?.kind === 'edit'
      ? (job.visits.find((visit) => visit.id === editor.visitId) ?? null)
      : null

  const windows = (
    <>
      <EmailCompose
        open={compose === 'email'}
        onClose={() => setCompose(null)}
        jobId={job.id}
        clientId={job.client.id}
        contacts={emailContacts(job)}
        files={job.attachments.map((file) => ({
          id: file.id,
          name: file.filename,
        }))}
        initial={{
          to: email ? [email] : [],
          subject: `Job ${formatJobNumber(job)}`,
        }}
      />
      <EmailCompose
        open={reportEmail !== null}
        onClose={() => setReportEmail(null)}
        jobId={job.id}
        clientId={job.client.id}
        contacts={emailContacts(job)}
        files={[
          ...job.attachments.map((file) => ({ id: file.id, name: file.filename })),
          ...(reportEmail && !job.attachments.some((file) => file.id === reportEmail.attachmentId)
            ? [{ id: reportEmail.attachmentId, name: reportEmail.filename }]
            : []),
        ]}
        initial={
          reportEmail
            ? reportEmailDraft(job, reportEmail, { name: primary?.name ?? null, email })
            : {}
        }
      />
      <SmsCompose
        open={compose === 'sms'}
        onClose={() => setCompose(null)}
        contactName={primary?.name ?? job.client.name}
        phone={phone}
        jobId={job.id}
      />
      <NoteCompose
        open={compose === 'note'}
        onClose={() => setCompose(null)}
        isAddingNote={addNote.isPending}
        onAddNote={(body, done) => addNote.mutate({ body, jobId: job.id }, { onSuccess: done })}
      />
      <BookingPopup
        open={canSchedule && editor !== null && (editor.kind === 'create' || editingVisit !== null)}
        visit={editingVisit}
        jobId={job.id}
        jobNumber={formatJobNumber(job)}
        onClose={() => setEditor(null)}
      />
      {repeat.data && repeatMode ? (
        <RepeatPopup
          open={canSchedule}
          mode={repeatMode}
          job={job}
          data={repeat.data}
          onClose={() => setRepeatMode(null)}
          onNewAgreement={
            can('CLIENT_EDIT')
              ? () => {
                  setRepeatMode(null)
                  setBuildingAgreement(true)
                }
              : undefined
          }
        />
      ) : null}
      {repeat.data && buildingAgreement && job.site ? (
        <AgreementBuilder
          open
          mode={{
            kind: 'new',
            client: { id: job.client.id, name: job.client.name },
            job: {
              id: job.id,
              number: formatJobNumber(job),
              status: job.status,
              siteId: job.site.id,
              dueOn: repeat.data.jobDueOn,
              services: job.services.map((service) => ({
                serviceTypeId: service.serviceType.id,
                name: service.serviceType.name,
                priceIncTax: service.priceIncTax ?? null,
              })),
              interval: repeat.data.imported?.interval ?? null,
            },
          }}
          onClose={() => setBuildingAgreement(false)}
        />
      ) : null}
    </>
  )

  const dropLayer = (
    <>
      {drop.over ? <DropVeil label={formatJobNumber(job)} /> : null}
      <DropStatus
        status={drop.status}
        onDismiss={drop.dismiss}
        onShowFiles={() => setTab('files')}
      />
    </>
  )

  /*
   * The phone.
   *
   * Nothing is pinned to the top: the number and the client scroll away like
   * any other content, because on a 390px screen a fixed header is a third of
   * the job spent saying which job it is. The tab strip sticks when it
   * reaches the top, so switching tab never needs a scroll back up. What is
   * pinned is the bottom — the actions take the place of the app's own tab bar
   * while the card is over it, which is the only bar a thumb can reach anyway.
   */
  if (narrow) {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col bg-white" {...drop.handlers}>
        {dropLayer}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-ink-50">
          <div className="bg-brand-800 px-4 py-2.5">
            <JobTitle job={job} suburb={suburb} size="phone" />
          </div>

          <div className="sticky top-0 z-10 flex items-center gap-1 overflow-x-auto border-b border-ink-200 bg-white/95 px-4 backdrop-blur">
            {tabs}
          </div>

          {tab === 'assistant' ? (
            <AssistantPane job={job} />
          ) : (
            <div className="flex flex-col px-4 py-1">
              {/* On a phone the property and how to get in lead: that is what
                  somebody standing outside it came here for. */}
              {tab === 'client' ? (
                <Panel>
                  <ClientRail job={job} />
                </Panel>
              ) : null}
              {panels}
              {tab === 'schedule' && scheduleRail ? <Panel>{scheduleRail}</Panel> : null}
            </div>
          )}
        </div>

        <div
          role="group"
          aria-label="Job actions"
          className="flex shrink-0 items-stretch gap-0.5 border-t border-ink-200 bg-white/95 px-1.5 pt-1.5 pb-[calc(0.375rem+var(--safe-bottom))] backdrop-blur"
        >
          {actions.map((action) => (
            <CardAction key={action.label} {...action} stacked />
          ))}
          <CardAction
            icon="activity"
            label="Activity"
            stacked
            onClick={() => setActivityOpen(true)}
          />
        </div>

        {activityOpen ? (
          <Sheet title="Activity" onClose={() => setActivityOpen(false)}>
            <aside aria-label="Activity" className="flex flex-col gap-3.5 bg-ink-50 px-4 py-4">
              {activity}
            </aside>
          </Sheet>
        ) : null}
        {proposalBuilder}
        {windows}
      </div>
    )
  }

  return (
    <div className={cn(frame, 'relative')} {...drop.handlers}>
      {dropLayer}
      <header className="flex shrink-0 flex-wrap items-center gap-x-3.5 gap-y-1.5 bg-brand-800 px-5 py-2">
        <span aria-hidden="true" className="flex shrink-0 items-center gap-3.5">
          <Wordmark onDark className="w-[40px]" />
          <span className="h-8 w-px bg-white/20" />
        </span>
        <JobTitle job={job} suburb={suburb} size="desk" />

        <div className="ml-auto flex flex-wrap items-center gap-1">
          {actions.map((action) => (
            <CardAction key={action.label} {...action} />
          ))}
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="ml-1 grid size-[var(--control-md)] shrink-0 place-items-center rounded-lg text-white transition-colors hover:bg-white/10"
            >
              <Icon name="close" className="size-5" />
            </button>
          ) : null}
        </div>
      </header>

      <div className="flex shrink-0 items-center gap-1 overflow-x-auto border-b border-ink-200 px-4">
        {tabs}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-white lg:flex-row lg:overflow-hidden">
        {tab === 'billing' ? (
          <BillingPane job={job} />
        ) : tab === 'assistant' ? (
          <AssistantPane job={job} />
        ) : (
          <>
            <section
              aria-label="Job details"
              className={cn(
                'flex min-w-0 flex-1 flex-col px-5 py-1 lg:overflow-y-auto',
                tab === 'work' && 'lg:border-r lg:border-ink-200',
              )}
            >
              {panels}
            </section>

            {tab === 'work' ? (
              <aside
                aria-label="Activity"
                className="flex shrink-0 flex-col gap-2.5 border-t border-ink-200 bg-ink-50 px-5 py-3.5 lg:w-[42%] lg:max-w-[560px] lg:min-w-[400px] lg:overflow-y-auto lg:border-t-0"
              >
                {activity}
              </aside>
            ) : tab === 'client' ? (
              <aside
                aria-label="Site and client"
                className="shrink-0 border-t border-ink-200 bg-ink-50 px-4 py-3 lg:w-[32%] lg:max-w-[360px] lg:min-w-[290px] lg:overflow-y-auto lg:border-t-0 lg:border-l"
              >
                <ClientRail job={job} />
              </aside>
            ) : tab === 'schedule' && scheduleRail ? (
              <aside
                aria-label={repeat.data?.agreement ? 'Agreement' : 'Repeat schedule'}
                className="flex shrink-0 flex-col border-t border-ink-200 bg-ink-50 px-4 py-3 lg:w-[32%] lg:max-w-[360px] lg:min-w-[290px] lg:overflow-y-auto lg:border-t-0 lg:border-l"
              >
                {scheduleRail}
              </aside>
            ) : null}
          </>
        )}
      </div>
      {proposalBuilder}
      {windows}
    </div>
  )
}

/**
 * What is still outstanding on the job, as the count beside Work.
 *
 * Services, checklist items and follow-ups together, because the distinction
 * between a service, the standing instructions and somebody's ad-hoc task
 * matters inside the tab and not on the tab itself. A skipped item counts as
 * dealt with — it was passed over on the record, with a reason.
 */
function openItems(job: Job): number {
  const items = (job.checklists ?? []).flatMap((checklist) => checklist.items)

  return (
    openServices(job) +
    items.filter((item) => item.completedAt === null && item.skippedAt === null).length +
    job.tasks.filter((task) => task.completedAt === null).length
  )
}

/**
 * Which job, on navy: the number, the kind of work as a tag, and the client
 * with the site's suburb under them.
 *
 * The tag only reads the category; the picker is in Job details and nowhere
 * else (decision 52). The heading is the client alone, so the dialog is named
 * by who the job is for and not by the suburb. ADR 025.
 */
function JobTitle({
  job,
  suburb,
  size,
}: {
  job: Job
  suburb: string | null
  size: 'desk' | 'phone'
}) {
  const phone = size === 'phone'

  return (
    <div className="flex min-w-0 flex-col">
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="shrink-0 font-display text-[20px] leading-tight font-extrabold tracking-[0.01em] text-white">
          {formatJobNumber(job)}
        </span>
        {job.category ? (
          <span className="min-w-0 truncate rounded bg-accent-500 px-1.5 py-px text-[10.5px] font-bold tracking-[0.08em] text-white uppercase">
            {job.category.name}
          </span>
        ) : null}
      </div>
      <div
        className={cn(
          'flex min-w-0 items-baseline font-medium text-sky',
          phone ? 'text-[14px]' : 'text-[13px]',
        )}
      >
        <h1 id="job-card-title" className="min-w-0 truncate">
          {job.client.name}
        </h1>
        {suburb ? <span className="shrink-0 whitespace-pre">{` · ${suburb}`}</span> : null}
      </div>
    </div>
  )
}

function TabButton({
  active,
  count,
  assistant,
  onClick,
  children,
}: {
  active: boolean
  count?: number
  /** The assistant's tab wears the brand blue and its sparkle. */
  assistant?: boolean
  onClick: () => void
  children: string
}) {
  return (
    <button
      type="button"
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
      className={cn(
        'flex min-h-10 items-center gap-1.5 px-3 text-[13.5px] whitespace-nowrap transition-colors',
        active
          ? 'font-bold text-brand-800 shadow-[inset_0_-3px_0_var(--color-accent-500)]'
          : assistant
            ? 'font-semibold text-brand-600 hover:text-brand-800'
            : 'font-semibold text-ink-600 hover:text-brand-800',
      )}
    >
      {assistant ? <Icon name="sparkles" className="size-4" /> : null}
      {children}
      {/* A real space text node, so the button is named "Files 3" rather than
          "Files3" to anything reading the accessible name out. */}
      {count ? ' ' : null}
      {count ? (
        <span
          className={cn(
            'rounded-full px-1.5 py-px text-[11px] font-bold tabular-nums',
            active ? 'bg-brand-800 text-white' : 'bg-brand-50 text-brand-800',
          )}
        >
          {count}
        </span>
      ) : null}
    </button>
  )
}

type ActionSpec = {
  icon: IconName
  label: string
  href?: string | null
  onClick?: (() => void) | undefined
  disabledTitle?: string
  /** The one action in Buster Red on the command bar. */
  primary?: boolean
}

/**
 * One of the card's actions, as a chip on the navy command bar or as a bar
 * button on a phone.
 *
 * An action with nothing behind it is disabled and says why rather than
 * disappearing, so neither the row nor the bar reflows between one job and the
 * next — a Call that moves under your thumb because this client has no mobile
 * number is worse than a Call that is greyed out.
 */
function CardAction({
  icon,
  label,
  href,
  onClick,
  disabledTitle,
  primary,
  stacked,
}: ActionSpec & {
  /** The bottom bar: icon over label, a fifth of the width, thumb-sized. */
  stacked?: boolean
}) {
  const live = Boolean(onClick || href)
  const className = stacked
    ? 'flex min-h-14 flex-1 basis-0 flex-col items-center justify-center gap-1 rounded-xl px-0.5 text-[10.5px] font-semibold text-ink-700 transition-colors active:bg-ink-100'
    : cn(
        'inline-flex min-h-[var(--control-md)] items-center gap-1.5 rounded-md text-[13px] transition-colors',
        !live
          ? 'px-2.5 font-semibold'
          : primary
            ? 'ml-1 bg-accent-500 px-3.5 font-bold text-white hover:bg-accent-700'
            : 'bg-white/[0.08] px-2.5 font-semibold text-white hover:bg-white/15',
      )
  const glyph = (
    <Icon
      name={icon}
      className={cn('shrink-0', stacked ? 'size-[19px] text-ink-500' : 'size-[17px]')}
    />
  )

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={className}>
        {glyph}
        {label}
      </button>
    )
  }

  if (href) {
    return (
      <a href={href} className={className}>
        {glyph}
        {label}
      </a>
    )
  }

  return (
    <span
      aria-disabled="true"
      title={disabledTitle ?? `No ${label === 'Email' ? 'email address' : 'number'} on file`}
      className={cn(
        className,
        'cursor-not-allowed',
        stacked ? 'text-ink-400 opacity-60' : 'text-white/45',
      )}
    >
      {glyph}
      {label}
    </span>
  )
}
